import * as XLSX from 'xlsx';

export interface OrderExportData {
  id?: number;
  order_number: string;
  created_at?: string;
  customer_name?: string;
  customer_phone?: string;
  shipping_address?: string;
  notes?: string;
  total_amount?: string | number;
  final_amount?: string | number;
  discount_amount?: string | number;
  shipping_cost?: string | number;
  status?: string;
  steadfast_tracking_code?: string;
  steadfast_consignment_id?: string;
  steadfast_status?: string;
  items?: Array<{
    product_name?: string;
    name?: string;
    quantity?: number;
    selected_color?: string;
    color?: string;
    selected_size?: string;
    size?: string;
    selected_weight?: string;
    weight?: string;
    [key: string]: any;
  }>;
  categoryLabel?: string;
  [key: string]: any;
}

/**
 * Format Note field for Mohammad Parcel:
 * Includes all item variant values (color, size, weight) and customer order note.
 * Example format: "color: black, size: 36, weight: 1kg, Note: deliver quickly"
 */
export const buildMohammadParcelNote = (order: OrderExportData): string => {
  const parts: string[] = [];
  const items = order.items || [];

  const itemVariantParts: string[] = [];
  items.forEach((item, index) => {
    const v: string[] = [];
    const color = item.selected_color || item.color;
    const size = item.selected_size || item.size;
    const weight = item.selected_weight || item.weight;

    if (color && String(color).trim()) {
      v.push(`color: ${String(color).trim()}`);
    }
    if (size && String(size).trim()) {
      v.push(`size: ${String(size).trim()}`);
    }
    if (weight && String(weight).trim()) {
      v.push(`weight: ${String(weight).trim()}`);
    }

    if (v.length > 0) {
      if (items.length > 1) {
        const name = item.product_name || item.name || `Item ${index + 1}`;
        itemVariantParts.push(`${name} (${v.join(', ')})`);
      } else {
        itemVariantParts.push(v.join(', '));
      }
    }
  });

  if (itemVariantParts.length > 0) {
    parts.push(itemVariantParts.join(', '));
  }

  const rawNote = (order.notes || order.note || '').trim();
  if (rawNote) {
    parts.push(`Note: ${rawNote}`);
  }

  return parts.join(', ');
};

/**
 * Export orders in "Mohammad Parcel" layout matching the reference template:
 * Columns: Invoice Number | Customer Name | Customer Phone | Customer Address | Cod Amount | Note
 * Sheet title: "Mohammad Parcel"
 */
export const exportMohammadParcelXLSX = (orders: OrderExportData[], customSuffix?: string) => {
  const headers = [
    'Invoice Number',
    'Customer Name',
    'Customer Phone',
    'Customer Address',
    'Cod Amount',
    'Note',
  ];

  const dataRows = orders.map((order) => {
    // COD amount calculation
    const cod =
      order.final_amount !== undefined && order.final_amount !== null
        ? parseFloat(String(order.final_amount))
        : (parseFloat(String(order.total_amount || 0)) - parseFloat(String(order.discount_amount || 0)));

    return [
      order.order_number || '',
      order.customer_name || '',
      order.customer_phone || '',
      order.shipping_address || '',
      isNaN(cod) ? 0 : cod,
      buildMohammadParcelNote(order),
    ];
  });

  const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);

  // Set column widths for presentation in Excel
  ws['!cols'] = [
    { wch: 18 }, // Invoice Number
    { wch: 22 }, // Customer Name
    { wch: 16 }, // Customer Phone
    { wch: 38 }, // Customer Address
    { wch: 14 }, // Cod Amount
    { wch: 42 }, // Note (expanded to comfortably display variants and note)
  ];

  const wb = XLSX.utils.book_new();
  // Name the worksheet tab "Mohammad Parcel"
  XLSX.utils.book_append_sheet(wb, ws, 'Mohammad Parcel');

  const today = new Date().toISOString().slice(0, 10);
  const suffix = customSuffix ? `_${customSuffix}` : '';
  XLSX.writeFile(wb, `Mohammad_Parcel${suffix}_${today}.xlsx`);
};

/**
 * Export orders in "Product Dealer Sheet" layout:
 * Columns: Invoice Number | Customer Name | Customer Phone | Note
 * Sheet title: "Product Dealer Sheet"
 */
export const exportProductDealerXLSX = (orders: OrderExportData[], customSuffix?: string) => {
  const headers = [
    'Invoice Number',
    'Customer Name',
    'Customer Phone',
    'Note',
  ];

  const dataRows = orders.map((order) => {
    return [
      order.order_number || '',
      order.customer_name || '',
      order.customer_phone || '',
      buildMohammadParcelNote(order),
    ];
  });

  const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);

  // Set column widths for presentation in Excel
  ws['!cols'] = [
    { wch: 18 }, // Invoice Number
    { wch: 24 }, // Customer Name
    { wch: 18 }, // Customer Phone
    { wch: 45 }, // Note
  ];

  const wb = XLSX.utils.book_new();
  // Name the worksheet tab "Product Dealer Sheet"
  XLSX.utils.book_append_sheet(wb, ws, 'Product Dealer Sheet');

  const today = new Date().toISOString().slice(0, 10);
  const suffix = customSuffix ? `_${customSuffix}` : '';
  XLSX.writeFile(wb, `Product_Dealer_Sheet${suffix}_${today}.xlsx`);
};

/**
 * Export full detailed orders in .xlsx format
 */
export const exportStandardXLSX = (
  orders: OrderExportData[],
  statusFilter = 'all',
  getCategoryLabel?: (order: any) => string
) => {
  const headers = [
    'Order #',
    'Order Date',
    'Product Title',
    'Color',
    'Size',
    'Weight',
    'Quantity',
    'Customer Name',
    'Phone',
    'Address',
    'Notes',
    'Category',
    'Total Amount',
    'Shipping Cost',
    'Status',
    'Steadfast Tracking',
    'Steadfast Consignment',
    'Steadfast Status',
  ];

  const dataRows = orders.map((order) => {
    const firstItem = order.items?.[0];
    const items = order.items || [];
    const productTitle =
      items.length === 0
        ? 'No items'
        : items.length === 1
        ? items[0].product_name || items[0].name || 'Unknown'
        : `${items[0].product_name || items[0].name || 'Unknown'} (+${items.length - 1} more)`;

    const orderDate = order.created_at
      ? new Date(order.created_at).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';

    const category = getCategoryLabel ? getCategoryLabel(order) : order.categoryLabel || '';

    return [
      order.order_number || '',
      orderDate,
      productTitle,
      firstItem?.selected_color || firstItem?.color || '',
      firstItem?.selected_size || firstItem?.size || '',
      firstItem?.selected_weight || firstItem?.weight || '',
      firstItem?.quantity || 1,
      order.customer_name || '',
      order.customer_phone || '',
      order.shipping_address || '',
      order.notes || '',
      category,
      parseFloat(String(order.total_amount || 0)).toFixed(2),
      parseFloat(String(order.shipping_cost || 0)).toFixed(2),
      order.status || '',
      order.steadfast_tracking_code || '',
      order.steadfast_consignment_id || '',
      order.steadfast_status || '',
    ];
  });

  const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
  ws['!cols'] = [
    { wch: 16 }, // Order #
    { wch: 20 }, // Order Date
    { wch: 30 }, // Product Title
    { wch: 12 }, // Color
    { wch: 12 }, // Size
    { wch: 12 }, // Weight
    { wch: 10 }, // Quantity
    { wch: 20 }, // Customer Name
    { wch: 15 }, // Phone
    { wch: 35 }, // Address
    { wch: 20 }, // Notes
    { wch: 16 }, // Category
    { wch: 14 }, // Total Amount
    { wch: 14 }, // Shipping Cost
    { wch: 14 }, // Status
    { wch: 20 }, // Steadfast Tracking
    { wch: 22 }, // Steadfast Consignment
    { wch: 16 }, // Steadfast Status
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Orders');

  const today = new Date().toISOString().slice(0, 10);
  const filterLabel = statusFilter !== 'all' ? `_${statusFilter}` : '';
  XLSX.writeFile(wb, `orders${filterLabel}_${today}.xlsx`);
};

/**
 * Standard CSV export (preserves original CSV download functionality)
 */
export const exportStandardCSV = (
  orders: OrderExportData[],
  statusFilter = 'all',
  getCategoryLabel?: (order: any) => string
) => {
  const escapeCSV = (val: any): string => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  };

  const headers = [
    'Order #',
    'Order Date',
    'Product Title',
    'Color',
    'Size',
    'Weight',
    'Quantity',
    'Customer Name',
    'Phone',
    'Address',
    'Notes',
    'Category',
    'Total Amount',
    'Shipping Cost',
    'Status',
    'Steadfast Tracking',
    'Steadfast Consignment',
    'Steadfast Status',
  ];

  const rows = orders.map((order) => {
    const firstItem = order.items?.[0];
    const items = order.items || [];
    const productTitle =
      items.length === 0
        ? 'No items'
        : items.length === 1
        ? items[0].product_name || items[0].name || 'Unknown'
        : `${items[0].product_name || items[0].name || 'Unknown'} (+${items.length - 1} more)`;

    const orderDate = order.created_at
      ? new Date(order.created_at).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';

    const category = getCategoryLabel ? getCategoryLabel(order) : order.categoryLabel || '';

    return [
      escapeCSV(order.order_number),
      escapeCSV(orderDate),
      escapeCSV(productTitle),
      escapeCSV(firstItem?.selected_color || firstItem?.color || ''),
      escapeCSV(firstItem?.selected_size || firstItem?.size || ''),
      escapeCSV(firstItem?.selected_weight || firstItem?.weight || ''),
      escapeCSV(firstItem?.quantity || 1),
      escapeCSV(order.customer_name),
      escapeCSV(order.customer_phone),
      escapeCSV(order.shipping_address),
      escapeCSV(order.notes),
      escapeCSV(category),
      escapeCSV(parseFloat(String(order.total_amount || 0)).toFixed(2)),
      escapeCSV(parseFloat(String(order.shipping_cost || 0)).toFixed(2)),
      escapeCSV(order.status),
      escapeCSV(order.steadfast_tracking_code || ''),
      escapeCSV(order.steadfast_consignment_id || ''),
      escapeCSV(order.steadfast_status || ''),
    ].join(',');
  });

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const today = new Date().toISOString().slice(0, 10);
  const filterLabel = statusFilter !== 'all' ? `_${statusFilter}` : '';
  a.href = url;
  a.download = `orders${filterLabel}_${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
